import React, { useEffect, useRef, useState } from 'react';
import { useDashboardTranslation } from '../../i18n';
import { Lock, Globe, Users, Search, X, Loader2, AlertTriangle } from 'lucide-react';
import type { WidgetAccessPerson } from '../../access/types';
import type { DashboardCategoryOption } from './categories';

/**
 * 🔴 **RETIRADO EL 2026-09-14 (F3).** El Constructor ya no pregunta la vertical: la **determina la
 * categoría** que se eligió en *Apariencia*.
 *
 * Se conserva el tipo exportado porque **no está huérfano**: el diálogo de acceso lo sigue usando para
 * ofrecer «que lo vea todo ventas» como sujeto de un grant, que es un acto distinto —repartir algo que
 * ya existe— y sí se pregunta. Lo que desapareció es el selector de ESTE paso.
 *
 * @deprecated para el Widget Builder. Para el diálogo de acceso, ver `WidgetAccessTerm`.
 */
export interface DashboardRoleOption {
  /** Valor que se guarda en `roles_permitidos` y que el backend cruza. */
  id: string;
  /** Texto que ve el administrador. */
  label: string;
}

/**
 * Vacío A PROPÓSITO, y ya casi sin consumidores: desde F3 el Builder no lo usa.
 *
 * Traía `admin | ventas | compras | inventario`, el vocabulario de Siesa Comercial dentro de una
 * librería compartida, y eso hacía que un host que olvidara declarar el suyo **segmentara sus widgets
 * con los módulos de otro producto** — sin error, y con una interfaz que se veía perfectamente normal.
 */
export const DEFAULT_AVAILABLE_ROLES: DashboardRoleOption[] = [];

/** Cómo se reparte un widget público, elegido al crearlo. */
export type RepartoInicial = 'module' | 'people';

interface Step5PermissionsProps {
  esPrivado: boolean;
  onUpdatePrivacy: (isPrivate: boolean) => void;

  /**
   * 🔴 **La vertical DERIVADA de la categoría.** `null` significa que el host no publicó la pareja
   * para esa categoría — y entonces publicar no se ofrece, porque no se sabe dónde.
   */
  vertical: DashboardCategoryOption | null;

  reparto: RepartoInicial;
  onUpdateReparto: (r: RepartoInicial) => void;

  personas: WidgetAccessPerson[];
  onUpdatePersonas: (p: WidgetAccessPerson[]) => void;

  /**
   * Buscador del host. **Es el mismo `searchPeople` del adaptador de acceso**, no uno nuevo: si el
   * host no lo declara, la opción «solo a estas personas» no se ofrece — teclear un id de usuario a
   * mano en un asistente de creación es la misma falla que el diálogo de acceso ya cerró.
   */
  searchPeople?: (query: string) => Promise<WidgetAccessPerson[]>;

  /**
   * Si el host puede restringir y conceder (`savePolicy` + `grant`). Sin las dos, repartir al crear no
   * se ofrece: el widget se crearía público y el segundo paso no llegaría nunca.
   */
  puedeRepartirAlCrear: boolean;
}

const nombreDe = (p: WidgetAccessPerson) => p.nombre?.trim() || p.email?.trim() || p.id;

export const Step5Permissions: React.FC<Step5PermissionsProps> = ({
  esPrivado,
  onUpdatePrivacy,
  vertical,
  reparto,
  onUpdateReparto,
  personas,
  onUpdatePersonas,
  searchPeople,
  puedeRepartirAlCrear,
}) => {
  const { t } = useDashboardTranslation();

  const [consulta, setConsulta] = useState('');
  const [resultados, setResultados] = useState<WidgetAccessPerson[]>([]);
  const [buscando, setBuscando] = useState(false);
  const ultimaBusqueda = useRef(0);

  /**
   * El buscador se reutiliza tal cual del adaptador de acceso. La lógica propia es descartar
   * respuestas fuera de orden: sin el contador, teclear rápido deja pintados los resultados de una
   * consulta anterior y la persona elegiría a alguien que no buscó.
   *
   * 🔴 **Dos cosas de este efecto son cicatrices de un spinner que no se apagaba nunca**, y las dos
   * hay que dejarlas como están:
   *
   * 1. **`setBuscando(true)` va DENTRO del `setTimeout`.** Estaba fuera, así que cada pulsación lo
   *    encendía y el `clearTimeout` del cleanup cancelaba la búsqueda antes de lanzarla: si el
   *    efecto se remonta —por lo que sea— el spinner queda encendido **para siempre**, porque nadie
   *    llega al `finally` que lo apaga. Dentro, un debounce cancelado no deja rastro.
   * 2. **El efecto NO depende de `searchPeople`.** Es una prop de función, y basta con que el
   *    llamador la construya en el render —un `.bind()`, una lambda— para que cambie de identidad en
   *    cada pasada y remonte el efecto en bucle. Se lee por `ref`, que es lo que se quiere: usar
   *    siempre la última sin que su identidad dispare nada.
   */
  const buscarRef = useRef(searchPeople);
  useEffect(() => { buscarRef.current = searchPeople; });

  useEffect(() => {
    const buscar = buscarRef.current;
    if (!buscar || consulta.trim().length < 2) {
      setResultados([]);
      setBuscando(false);
      return;
    }
    const turno = ++ultimaBusqueda.current;
    const id = setTimeout(() => {
      setBuscando(true);
      buscar(consulta.trim())
        .then(r => {
          if (turno === ultimaBusqueda.current) setResultados(r);
        })
        .catch(() => {
          if (turno === ultimaBusqueda.current) setResultados([]);
        })
        .finally(() => {
          if (turno === ultimaBusqueda.current) setBuscando(false);
        });
    }, 250);
    return () => clearTimeout(id);
  }, [consulta]);

  const ofrecePersonas = puedeRepartirAlCrear && !!searchPeople;
  const agregar = (p: WidgetAccessPerson) => {
    if (!personas.some(x => x.id === p.id)) onUpdatePersonas([...personas, p]);
    setConsulta('');
    setResultados([]);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h3 className="text-lg font-semibold text-gray-800">{t('dashboard.builder.step5.title')}</h3>

      <div className="p-4 border border-gray-200 rounded-xl bg-white space-y-4">
        {/* ── Privado ─────────────────────────────────────────────────────────────────────── */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            name="privacidad"
            checked={esPrivado === true}
            onChange={() => onUpdatePrivacy(true)}
            className="mt-1 text-blue-600 focus:ring-blue-500"
          />
          <div className="flex items-start gap-2">
            <Lock className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
            <div>
              <span className="block font-medium text-gray-800">{t('dashboard.builder.step5.private')}</span>
              <span className="text-sm text-gray-500">{t('dashboard.builder.step5.privateHint')}</span>
            </div>
          </div>
        </label>

        {/* ── Público EN la vertical ──────────────────────────────────────────────────────── */}
        {/*
          🔴 El rótulo NO es cosmético. Decía «Público» a secas, que sugiere «todo el mundo» y NUNCA
          fue cierto: siempre fue «todo el mundo DE ESTA VERTICAL» —el techo de módulo se aplica
          igual—. Decirlo mal es la mitad de por qué este mecanismo se malinterpretaba.
        */}
        <label
          className={`flex items-start gap-3 border-t border-gray-100 pt-4 ${
            vertical ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
          }`}
        >
          <input
            type="radio"
            name="privacidad"
            checked={esPrivado === false}
            disabled={!vertical}
            onChange={() => onUpdatePrivacy(false)}
            className="mt-1 text-blue-600 focus:ring-blue-500"
          />
          <div className="flex items-start gap-2">
            <Globe className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
            <div>
              <span className="block font-medium text-gray-800">
                {vertical
                  ? t('dashboard.builder.step5.publicIn', { alcance: vertical.label })
                  : t('dashboard.builder.step5.public')}
              </span>
              <span className="text-sm text-gray-500">
                {vertical
                  ? t('dashboard.builder.step5.publicInHint', { alcance: vertical.label })
                  : t('dashboard.builder.step5.noScopeHint')}
              </span>
            </div>
          </div>
        </label>

        {/* ── A quién, dentro de la vertical ──────────────────────────────────────────────── */}
        {esPrivado === false && vertical && (
          <div className="ml-8 mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="reparto"
                checked={reparto === 'module'}
                onChange={() => onUpdateReparto('module')}
                className="mt-0.5 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-800">
                {t('dashboard.builder.step5.toEveryone', { alcance: vertical.label })}
              </span>
            </label>

            <label
              className={`flex items-start gap-3 ${ofrecePersonas ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
            >
              <input
                type="radio"
                name="reparto"
                checked={reparto === 'people'}
                disabled={!ofrecePersonas}
                onChange={() => onUpdateReparto('people')}
                className="mt-0.5 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-800">
                {t('dashboard.builder.step5.toPeople')}
                {!ofrecePersonas && (
                  <span className="block text-xs text-gray-500">{t('dashboard.builder.step5.noSearch')}</span>
                )}
              </span>
            </label>

            {reparto === 'people' && ofrecePersonas && (
              <div className="ml-7 space-y-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
                  <input
                    type="text"
                    value={consulta}
                    onChange={e => setConsulta(e.target.value)}
                    placeholder={t('dashboard.builder.step5.searchPerson')}
                    aria-label={t('dashboard.builder.step5.searchPerson')}
                    className="w-full pl-8 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  {buscando && <Loader2 className="w-4 h-4 absolute right-2.5 top-2.5 text-gray-400 animate-spin" />}
                </div>

                {resultados.length > 0 && (
                  <ul className="border border-gray-200 rounded-lg bg-white divide-y divide-gray-100 max-h-40 overflow-y-auto">
                    {resultados.map(p => (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => agregar(p)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
                        >
                          {nombreDe(p)}
                          {p.email && <span className="text-xs text-gray-400 ml-2">{p.email}</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {personas.length > 0 && (
                  <ul className="flex flex-wrap gap-2">
                    {personas.map(p => (
                      <li
                        key={p.id}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 border border-blue-300"
                      >
                        <Users className="w-3 h-3" />
                        {nombreDe(p)}
                        <button
                          type="button"
                          onClick={() => onUpdatePersonas(personas.filter(x => x.id !== p.id))}
                          aria-label={t('dashboard.builder.step5.removePerson', { persona: nombreDe(p) })}
                          className="hover:text-blue-950"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {/*
                  🔴 Se dice ANTES de guardar, no después. Son tres llamadas sin transacción —crear,
                  restringir, conceder— y quien elige esta opción tiene que saber que el reparto es un
                  segundo acto que puede fallar por su cuenta.
                */}
                {personas.length === 0 && (
                  <p className="text-[11px] text-gray-500 italic">
                    {t('dashboard.builder.step5.noPeopleYet', { alcance: vertical.label })}
                  </p>
                )}

                <p className="text-[11px] text-gray-500 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-px flex-shrink-0 text-amber-500" />
                  <span>{t('dashboard.builder.step5.ceilingWarning', { alcance: vertical.label })}</span>
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
